import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { buildRoomPackageOptionsViewOrder, filterPackageOptionsBySkiPackageType } from 'lib/offer/offerPageUtil'
import { getCheapestSelectedPackageOption, getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlashHotelRoomOptionVariant from './FlashHotelRoomOptionVariant'
import { InOfferFlowContext } from './RoomOptionsContext'
import Pane from 'components/Common/Pane'
import Divider from 'LuxKit/Divider'
import { max } from 'lib/array/arrayUtils'

const PackagesLayoutContainer = styled(LayoutContainer)`
  padding: 0;
`

interface Props {
  offer: App.HotelOffer;
  cheapestSelectedOption?: OfferPackageOptionView;
  packageOptions: Array<OfferPackageOptionView>;
}

function FlashHotelPackageOptionsVariant(props: Props) {
  const {
    offer,
    packageOptions: basePackageOptions,
    cheapestSelectedOption,
  } = props

  const packageStartRef = useRef<HTMLDivElement>(null)

  // TEMP: Remove this logic when "006RF00000IRdvlYAD" becomes unavailable
  const { packageOptions, skiPackageFiltered } = useMemo(() => {
    return filterPackageOptionsBySkiPackageType(basePackageOptions, cheapestSelectedOption)
  }, [basePackageOptions, cheapestSelectedOption])

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  }, [packageOptions])

  return (
    <VerticalSpacer gap={16}>
      <LayoutContainer>
        <VerticalSpacer gap={20}>
          <Divider kind="primary" />
          <Heading variant="heading3">Room options</Heading>
        </VerticalSpacer>
      </LayoutContainer>

      <PackagesLayoutContainer>
        <InOfferFlowContext.Provider value>
          <Pane background="neutral-six">
            <VerticalSpacer gap={8} ref={packageStartRef}>
              {packageOptionsByRoom.map((item) =>
                <FlashHotelRoomOptionVariant
                  key={item.roomId}
                  offer={offer}
                  packageViews={item.packageViews}
                  detailsModalMode="room-and-package"
                  availableRoomCount={availableRoomCountByRoomId.get(item.roomId)}
                  useSkiDealAvailabilityActions={skiPackageFiltered}
                />,
              )}
            </VerticalSpacer>
          </Pane>
        </InOfferFlowContext.Provider>
      </PackagesLayoutContainer>
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  offer: state.offer,
  packageOptions: getDurationPackageOptions(state, appState),
}), FlashHotelPackageOptionsVariant)
